import * as Controls from "VSS/Controls";
import Resources = require("Widgets/Scripts/Resources/TFS.Resources.Widgets");
import { Selector } from "Dashboards/Scripts/Selector";
import { ToggleableNumericInput, ToggleableNumericInputOptions } from "Widgets/Scripts/Shared/NumericInput";
import { Checkbox, CheckboxOptions } from "../Shared/Checkbox";
import FeatureAvailability_Services = require("VSS/FeatureAvailability/Services");

/**Describes values of panel. This contract is used for initialization and exposing state. */
export interface AdvancedFeaturesPanelValues {
    isPlannedWorkEnabled: boolean;
    plannedWorkStartOffset: number;

    isLateWorkEnabled: boolean;
    lateWorkDeadlineOffset: number;

    showResolvedItemsAsCompleted: boolean;
}

export interface AdvancedFeaturesPanelOptions extends Controls.EnhancementOptions, AdvancedFeaturesPanelValues {
    onChange: () => void;
}

/** Implements Velocity Advanced features config as a linear composition of controls in a stack layout. */
export class AdvancedFeaturesPanel extends Controls.Control<AdvancedFeaturesPanelOptions> implements Selector {
    private plannedWorkControl: ToggleableNumericInput;
    private lateWorkControl: ToggleableNumericInput;
    private showResolvedItemsAsCompleteControl: Checkbox;

    // feature flag
    private static readonly showResolvedItemsAsCompletedFeatureFlag = "WebAccess.Widgets.ShowResolvedItemsAsCompleted";

    constructor(options: AdvancedFeaturesPanelOptions) {
        super(options);
    }

    public initializeOptions(options: AdvancedFeaturesPanelOptions) {
        options.cssClass = "advanced-velocity-options-stackpanel";        
        super.initializeOptions(options);
    }

    public initialize() {
        let plannedWorkBlockOptions: ToggleableNumericInputOptions = {
            toggleLabel: Resources.VelocityConfig_DisplayPlannedWorkHeader,
            toggleOn: this._options.isPlannedWorkEnabled,

            inputLabel: Resources.VelocityConfig_PlannedWorkBodyText,
            inputValue: this._options.plannedWorkStartOffset,

            onChange: () => { this._options.onChange(); },

            minValue: 0
        };

        let lateWorkBlockOptions: ToggleableNumericInputOptions = {
            toggleLabel: Resources.VelocityConfig_DisplayCompletedLateWorkHeader,
            toggleOn: this._options.isLateWorkEnabled,

            inputLabel: Resources.VelocityConfig_LateWorkBodyText,
            inputValue: this._options.lateWorkDeadlineOffset,

            onChange: () => { this._options.onChange(); },

            minValue: 0
        };

        this.plannedWorkControl = ToggleableNumericInput.create(ToggleableNumericInput, this.getElement(), plannedWorkBlockOptions);
        this.lateWorkControl = ToggleableNumericInput.create(ToggleableNumericInput, this.getElement(), lateWorkBlockOptions);

        /**
         * Add checkbox if 'ShowResolvedItemsAsCompleted' feature flag is turned on
         */
        if(FeatureAvailability_Services.FeatureAvailabilityService.isFeatureEnabled(AdvancedFeaturesPanel.showResolvedItemsAsCompletedFeatureFlag, false)){

            let showResolvedItemsAsCompleteOptions: CheckboxOptions = {
                onChange: () => { this._options.onChange(); },
                checkboxLabel: Resources.ShowResolvedItemsAsCompletedLabel,
                toolTipText: Resources.ShowResolvedItemsAsCompletedToolTipText
            }

            this.showResolvedItemsAsCompleteControl = new Checkbox(this.getElement(), showResolvedItemsAsCompleteOptions);
            this.showResolvedItemsAsCompleteControl.setChecked(this._options.showResolvedItemsAsCompleted)
        }
    }

    public validate(): string {
        var result = this.plannedWorkControl.validate() || this.lateWorkControl.validate();

        if(FeatureAvailability_Services.FeatureAvailabilityService.isFeatureEnabled(AdvancedFeaturesPanel.showResolvedItemsAsCompletedFeatureFlag, false)){
            result = result || this.showResolvedItemsAsCompleteControl.validate();
        }

        return result;
    }

    public setEnabled(value: boolean): void {
        this.plannedWorkControl.setEnabled(value);
        this.lateWorkControl.setEnabled(value);
        this.showResolvedItemsAsCompleteControl.setEnabled(value);
    }

    public getSettings(): AdvancedFeaturesPanelValues {
        let plannedWorkSettings = this.plannedWorkControl.getSettings();
        let lateWorkSettings = this.lateWorkControl.getSettings();

        /**
         * Override value to false if FF is disabled
         * else set value based on widget settings
         */
        let showResolvedItemsAsCompletedSettings = FeatureAvailability_Services.FeatureAvailabilityService.isFeatureEnabled(AdvancedFeaturesPanel.showResolvedItemsAsCompletedFeatureFlag, false) ? 
        this.showResolvedItemsAsCompleteControl.getSettings() :
        false;

        return {
            isPlannedWorkEnabled: plannedWorkSettings.toggleOn,
            plannedWorkStartOffset: plannedWorkSettings.inputValue,
            isLateWorkEnabled: lateWorkSettings.toggleOn,
            lateWorkDeadlineOffset: lateWorkSettings.inputValue,
            showResolvedItemsAsCompleted: showResolvedItemsAsCompletedSettings
        };
    }
}